import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError, authApi } from "@/lib/api";
import { useAuth } from "@/lib/useAuth";
import { Button } from "@/components/ui/Button";
import { Field, Input } from "@/components/ui/Field";
import { Alert } from "@/components/ui/Alert";

export function InitAdminPage(): JSX.Element {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      await authApi.initAdmin(username.trim(), password);
      setInfo("管理员已创建, 正在登录…");
      await login(username.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "admin_exists") {
          setError("系统已有管理员, 请直接登录");
        } else {
          setError(err.message);
        }
      } else {
        setError("初始化失败, 请稍后重试");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-zinc-50 px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">初始化管理员</h1>
          <p className="mt-1 text-sm text-zinc-500">仅在首次部署且无任何管理员时可用</p>
        </div>
        <form onSubmit={onSubmit} className="space-y-5 rounded-xl border border-zinc-200 bg-white p-6 shadow-sm">
          {error ? <Alert tone="error">{error}</Alert> : null}
          {info ? <Alert tone="success">{info}</Alert> : null}
          <Field id="admin-username" label="管理员用户名" required hint="3 – 32 字符">
            <Input
              id="admin-username"
              name="username"
              autoComplete="username"
              required
              minLength={3}
              maxLength={32}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={submitting}
            />
          </Field>
          <Field id="admin-password" label="密码" required hint="至少 8 位">
            <Input
              id="admin-password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              maxLength={128}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />
          </Field>
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? "创建中…" : "创建并登录"}
          </Button>
          <p className="text-center text-xs text-zinc-500">
            已有账号?{" "}
            <Link to="/login" className="font-medium text-zinc-900 hover:underline">
              返回登录
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
